import { useState } from 'react';
import { countdownPhase, type ReplyWindowState, type WindowRefusal } from '../conversation';
import { useStatusMessage, type StatusMessage } from '../details/use-status-message';
import { DRAFT_KEPT_TEXT } from './ReplyWindowNotice';

export const CLOSED_ANNOUNCEMENT =
  'A janela de resposta foi encerrada. Você só poderá responder quando o cliente escrever de novo.';
export const REOPENED_ANNOUNCEMENT = 'A janela de resposta foi reaberta. Você já pode responder.';
export const SOON_ANNOUNCEMENT = 'A janela de resposta fecha em menos de 2 horas.';
export const URGENT_ANNOUNCEMENT = 'A janela de resposta fecha em menos de 10 minutos.';

type Phase = 'none' | 'far' | 'soon' | 'urgent' | 'closed';

const phaseOf = (state: ReplyWindowState): Phase =>
  state.kind === 'open'
    ? countdownPhase(state.remainingMs)
    : state.kind === 'none'
      ? 'none'
      : 'closed';

const isOpen = (phase: Phase): boolean => phase === 'far' || phase === 'soon' || phase === 'urgent';

const refusalAnnouncement = (refusal: WindowRefusal): string =>
  `A mensagem não foi enviada. ${refusal.message}${refusal.draftKept ? ` ${DRAFT_KEPT_TEXT}` : ''}`;

/**
 * O que dizer ao leitor de tela quando a janela muda ENQUANTO a conversa está aberta (nada é
 * anunciado ao abrir a conversa: o aviso e a contagem já estão na página para quem os lê).
 *
 * Só quatro anúncios, todos educados, e NENHUM por tique da contagem (que atualiza a cada 30 s):
 * 1. a janela fechou; 2. a janela reabriu (o cliente escreveu); 3. faltam menos de 2 h (cruzou o
 * limite da contagem); 4. faltam menos de 10 min. Se a recusa do servidor chegou junto com o
 * fechamento, só a recusa é dita (ela já explica que a janela fechou).
 */
export function announcementFor(
  before: Phase,
  after: Phase,
  refusal: WindowRefusal | null,
): string {
  if (refusal) return refusalAnnouncement(refusal);
  if (after === 'closed' && isOpen(before)) return CLOSED_ANNOUNCEMENT;
  if (before === 'closed' && isOpen(after)) return REOPENED_ANNOUNCEMENT;
  if (after === 'soon' && before === 'far') return SOON_ANNOUNCEMENT;
  if (after === 'urgent' && (before === 'far' || before === 'soon')) return URGENT_ANNOUNCEMENT;
  return '';
}

/**
 * Mensagem para a região `role="status"` do chat (que existe sempre, vazia, antes de qualquer
 * texto). A comparação com o estado anterior é feita durante a renderização, como no restante do
 * app, para não haver um quadro com o aviso novo e o anúncio velho.
 */
export function useWindowAnnouncements(state: ReplyWindowState): StatusMessage {
  const { message, announce } = useStatusMessage();
  const phase = phaseOf(state);
  const refusal = state.kind === 'closed' ? state.refusal : null;
  const refusalAt = refusal?.at ?? null;
  const [seen, setSeen] = useState({ phase, refusalAt });

  if (seen.phase !== phase || seen.refusalAt !== refusalAt) {
    setSeen({ phase, refusalAt });
    const text = announcementFor(seen.phase, phase, seen.refusalAt !== refusalAt ? refusal : null);
    if (text) announce(text);
  }
  return message;
}
